<script setup lang="ts">
import type { Entry } from "contentful";

// Composables
const localePath = useLocalePath();
const bookingWidgetElement = useTemplateRef("bookingWidget");
const { top } = useElementBounding(bookingWidgetElement);
const { navigationState } = useNavigation();

const {
  state,
  locations = undefined,
  isSticky = true,
} = defineProps<{
  state?: TBookingWidgetState;
  locations?: Entry | Entry[];
  submitLabel?: string;
  isSticky?: boolean;
  disabledLocation?: boolean;
}>();

const pageHeaderHeight = ref<number>(-1);

useResizeObserver(bookingWidgetElement, () => {
  updatePageHeaderHeight();
});

const travelDataStore = useTravelDataStore();

if (state == "prefilled location" && locations) {
  // This lets the widget preselect the location
  // @ts-expect-error because the state is not typed and initalized with "undefined", type is not correctly inferred as string or undefined
  // Please note that the current implementation of HotelSearchBar does not support mixed / multiple hotel codes
  travelDataStore.hotelCodes = Array.isArray(locations)
    ? locations[0].fields.code
    : locations.fields.code;
}

travelDataStore.checkTraveldata();

function updatePageHeaderHeight() {
  if (import.meta.server) {
    return;
  }

  const pageHeader = document.querySelector("header.page-header");

  if (!pageHeader) {
    import.meta.dev &&
      console.error(
        "page header not found, could not calculate the correct height",
      );

    return;
  }

  pageHeaderHeight.value = pageHeader.clientHeight;
}
</script>

<template>
  <div
    ref="bookingWidget"
    :class="[
      'ctf-booking-widget',
      { 'ctf-booking-widget--sticky': isSticky },
      {
        'ctf-booking-widget--shadow': isSticky && top <= pageHeaderHeight,
      },
      {
        'ctf-booking-widget--behind':
          (isSticky && navigationState.openedNavigationDropDownId) ||
          navigationState.mobileDrawerOpen,
      },
    ]"
  >
    <HotelSearchBar
      :class="[{ 'hotel-search-bar--sticky': isSticky }]"
      bar
      :has-shadow="!isSticky"
      :submit-url="localePath('/book')"
      :submitLabel
      :disabledLocation
    />
  </div>
</template>

<style scoped lang="scss">
.ctf-booking-widget {
  $self: &;

  &--sticky {
    @include md {
      position: sticky;
      top: var(--header-navigation-height-mobile, 60px);
      z-index: 20;
      background-color: var(--primary-white, #fff);
      padding-block: 1rem;

      &#{ $self }--shadow {
        &::after {
          position: fixed;
          top: var(--header-navigation-height-mobile, 60px);
          left: 0;
          z-index: -1;
          width: 100vw;
          height: 94px;
          background-color: var(--primary-white, #fff);
          box-shadow: rgb(0 0 0 / 6%) 0 16px 16px 0;
          transition:
            box-shadow 0.3s,
            border-top-color 0.3s;
          content: "";

          @include lg {
            top: var(--header-navigation-height, 80px);
          }
        }
      }
    }

    @include lg {
      top: var(--header-navigation-height, 80px);
    }

    &#{ $self }--behind {
      z-index: 0;
    }
  }

  .hotel-search-bar {
    &--sticky {
      max-width: var(--max-width, 1200px);
      margin: 0 auto;
    }
  }
}
</style>
